<template>
    <div id="store-credit-card-form">
        <div v-if="fetched">
            <div v-if="card !== null">
                <div class="row">
                    <div class="col-12 col-md-3">カード番号</div>
                    <div class="col-12 col-md-9">{{ `**** **** ${card.last4}` }}</div>
                </div>
                <hr>
                <div class="row">
                    <div class="col-12 col-md-3">カード名義</div>
                    <div class="col-12 col-md-9">{{ card.name }}</div>
                </div>
                <hr>
                <div class="row">
                    <div class="col-12 col-md-3">有効期限</div>
                    <div class="col-12 col-md-9">{{ `${card.exp_year}年${card.exp_month}月` }}</div>
                    <div v-if="isExp" class="col-12 mt-1 small">
                        <span class="vc-notification__text text-warning"><b-icon-exclamation-triangle-fill/></span>
                        <span class="text-muted">有効期限が近づいています。{{ $locale.consultation_host }}当日まで有効なクレジットカードですか？（<a href="/invoices" class="changeCreditCardLink">クレジットカードを変更</a>）</span>
                    </div>
                </div>
            </div>
            <div v-else>
                <div
                    v-show="schedule !== null"
                    class="vc-box mb-3 small font-weight-bold">
                    予約にはクレジットカードの登録が必要です。
                </div>

                <form @submit.stop.prevent="store">
                    <div class="form-group">
                        <label>カード番号</label>
                        <div id="el-card-number"></div>
                    </div>

                    <div class="form-group">
                        <label>有効期限</label>
                        <div id="el-card-expiry"></div>
                    </div>

                    <div class="form-group">
                        <label>カード名義</label>
                        <input
                            v-model="name"
                            id="name"
                            class="form-control m-input"
                            :class="{ 'is-invalid' : errors.name }"
                            type="text"
                            :placeholder="`${$locale.appName} TARO`">
                        <div v-if="errors.name" class="invalid-feedback d-block mt-1">
                            {{ errors.name[0] }}
                        </div>
                    </div>

                    <div class="form-group">
                        <label>セキュリティコード</label>
                        <div id="el-card-cvc"></div>
                    </div>

                    <div v-if="errors.message"
                         class="invalid-feedback d-block">
                        {{ errors.message[0] }}
                    </div>

                    <div class="d-flex justify-content-center">
                        <b-button
                            type="submit"
                            variant="yokumiru-orange"
                            :disabled="inProgress">
                            クレジットカードを登録
                        </b-button>
                    </div>
                </form>
            </div>
        </div>
        <div
            v-else
            class="text-center p-4">
            <b-spinner variant="yokumiru-user"></b-spinner>
        </div>
    </div>
</template>

<script>
    import constants from '../../constants'
    import {loadStripe} from '@stripe/stripe-js'

    export default {
        name: "StoreCreditCardForm",

        props: {
            dataPubkey: {
                type: String,
                required: true,
            },
            schedule: {
                type: Object,
                default: null,
            },
        },

        data: function () {
            return {
                pubkey: null,
                card: null,
                fetched: false,
                stripe: null,
                elements: null,
                name: null,
                cardNumber: null,
                cardExpiry: null,
                cardCvc: null,
                intent: null,
                inProgress :false,
                errors: {},
            }
        },

        computed: {
            isExp: function () {
                if (!this.schedule || !this.card) return false

                const schedule = this.schedule.date.clone()
                const year = Number(schedule.format('YYYY'))
                const month = Number(schedule.format('MM'))
                const day = Number(schedule.format('DD'))
                const endOfMonth = Number(schedule.endOf('month').format('DD'))

                return year > this.card.exp_year
                    || (year === this.card.exp_year && month > this.card.exp_month)
                    || (year === this.card.exp_year && month === this.card.exp_month && day === endOfMonth)
            },
        },

        created: function () {
            this.pubkey = this.dataPubkey
            bus.$on('init-store-credit-card-form', this.init)
        },

        beforeDestroy: function () {
            bus.$off('init-store-credit-card-form')
        },

        methods: {
            /**
             * カード取得
             */
            fetchCreditCard: function () {
                this.fetched = false

                axios.get(constants.user.credit_cards.endpoint.base)
                    .then(({data}) => {
                        this.card = data.data
                        this.fetched = true

                        if (this.card !== null) {
                            bus.$emit('has-credit-card')
                        }
                    })
            },

            /**
             * 登録完了後のカード再取得
             *
             * @param {string} pm_id
             */
            fetchCreditCardByPaymentMethod: function (pm_id) {
                this.fetched = false

                axios.get(`${constants.user.credit_cards.endpoint.base}/${pm_id}`)
                    .then(({data}) => {
                        this.card = data.data
                        this.fetched = true

                        if (this.card !== null) {
                            bus.$emit('has-credit-card')
                        }
                    })
            },

            /**
             * 初期化
             */
            init: async function () {
                await this.fetchCreditCard()
                await this.initForm()
                bus.$emit('init-store-credit-card-form-completed')
            },

            /**
             * フォーム初期化
             */
            initForm: async function () {
                this.stripe = await loadStripe(this.pubkey, {locale: "ja"})
                this.elements = this.stripe.elements({locale: "ja"})

                this.cardNumber = this.elements.create('cardNumber', {
                    classes: {base: 'StripeElement form-control form-control--stripe'},
                    showIcon: true,
                })
                this.cardExpiry = this.elements.create('cardExpiry', {
                    classes: {base: 'StripeElement form-control form-control--stripe'},
                })
                this.cardCvc = this.elements.create('cardCvc', {
                    classes: {base: 'StripeElement form-control form-control--stripe'},
                })

                let timer = null
                timer = setInterval(() => {
                    const isMounted = !!document.getElementById('el-card-number')
                        && !!document.getElementById('el-card-expiry')
                        && !!document.getElementById('el-card-cvc')

                    if (isMounted) {
                        clearInterval(timer)
                        this.cardNumber.mount('#el-card-number')
                        this.cardExpiry.mount('#el-card-expiry')
                        this.cardCvc.mount('#el-card-cvc')
                    }
                }, 100)
            },

            store: async function () {
                if (this.inProgress) return
                this.inProgress = true

                if (!this.intent) {
                    await axios.get(constants.user.credit_cards.endpoint.setup_intent)
                        .then(({data}) => {
                            this.intent = data.data
                        })
                }

                this.stripe
                    .confirmCardSetup(this.intent.client_secret, {
                        payment_method: {
                            card: this.elements.getElement('cardNumber'),
                            billing_details: {
                                name: this.name,
                            },
                        },
                    })
                    .then(result => {
                        if (result.setupIntent && result.setupIntent.status === "succeeded") {
                            this.clearSetupCardState()
                            this.fetchCreditCardByPaymentMethod(result.setupIntent.payment_method)
                            bus.$emit('has-credit-card')
                            bus.$emit('alert-success', 'カードを登録しました。')
                            this.errors = {}
                            this.inProgress = false
                        }
                        else {
                            this.errors.message = [result.error.message]
                            this.inProgress = false
                        }
                    })
            },

            /**
             * カード情報入力状態の初期化
             */
            clearSetupCardState: function () {
                this.intent = null
                this.cardNumber.clear()
                this.cardExpiry.clear()
                this.cardCvc.clear()
                this.name = null
            },
        },
    }
</script>

<style scoped lang="scss">
.changeCreditCardLink {
    text-decoration: underline;

    &:hover {
        text-decoration: none;
    }
}

.vc-box {
    padding: 12px 15px;
    background: #ebebeb;
}
</style>

<style lang="scss">
.form-control {
    &.form-control--stripe {
        padding-top: 0.6rem !important;
    }
}
</style>
